import React, { useCallback, useEffect, useRef, useState } from 'react'
import { Plus, Loader2, Film, AlertTriangle, X, CheckCircle2, Clock, Play, CheckSquare, Archive, ArchiveRestore, SlidersHorizontal, Pencil, RotateCcw } from 'lucide-react'
import { Tooltip } from '../ui'
import { useToast } from '../../contexts/ToastContext'
import broadcastAdminService, {
  Broadcast, BroadcastStatus, BroadcastPreflight
} from '../../services/broadcastAdminService'
import BroadcastUploadForm from './BroadcastUploadForm'
import BroadcastMeasurementDrawer from './BroadcastMeasurementDrawer'

interface Props {
  customerId: string
  organizationName?: string
  /** read-only hides upload entirely — customers never ingest, support does */
  readOnly?: boolean
  /** Jump to the Review tab with THIS broadcast selected. Omitted when the
   *  library is rendered somewhere with no review tab to jump to. */
  onReview?: (b: Broadcast) => void
}

const STATUS_STYLE: Record<string, { label: string; cls: string; icon: React.ComponentType<{ className?: string }> }> = {
  uploaded:          { label: 'Uploaded',   cls: 'bg-gray-100 text-gray-700',       icon: Clock },
  queued:            { label: 'Queued',     cls: 'bg-blue-100 text-blue-700',       icon: Clock },
  estimating:        { label: 'Estimating', cls: 'bg-blue-100 text-blue-700',       icon: Loader2 },
  awaiting_approval: { label: 'Needs approval', cls: 'bg-amber-100 text-amber-800', icon: AlertTriangle },
  sampling:          { label: 'Sampling',   cls: 'bg-indigo-100 text-indigo-700',   icon: Loader2 },
  detecting:         { label: 'Detecting',  cls: 'bg-indigo-100 text-indigo-700',   icon: Loader2 },
  aggregating:       { label: 'Aggregating',cls: 'bg-indigo-100 text-indigo-700',   icon: Loader2 },
  capturing:         { label: 'Capturing',  cls: 'bg-indigo-100 text-indigo-700',   icon: Loader2 },
  review:            { label: 'In review',  cls: 'bg-amber-100 text-amber-800',     icon: AlertTriangle },
  published:         { label: 'Published',  cls: 'bg-emerald-100 text-emerald-700', icon: CheckCircle2 },
  failed:            { label: 'Failed',     cls: 'bg-red-100 text-red-700',         icon: X },
  halted:            { label: 'Halted',     cls: 'bg-red-100 text-red-700',         icon: X },
  archived:          { label: 'Archived',   cls: 'bg-gray-200 text-gray-600',       icon: Archive }
}

const fmtDuration = (ms: number | null) => {
  if (!ms) return '—'
  const h = Math.floor(ms / 3_600_000)
  const m = Math.round((ms % 3_600_000) / 60_000)
  return h > 0 ? `${h}h ${m}m` : `${m}m`
}
const fmtSize = (b: number | null) => (b ? `${(b / 1e9).toFixed(2)} GB` : '—')
const fmtDate = (d: string | null) =>
  d ? new Date(d).toLocaleDateString(undefined, { day: 'numeric', month: 'short', year: 'numeric' }) : '—'

const StatusBadge: React.FC<{ status: BroadcastStatus }> = ({ status }) => {
  const s = STATUS_STYLE[status] ?? { label: status, cls: 'bg-gray-100 text-gray-700', icon: Clock }
  const Icon = s.icon
  const spin = ['estimating', 'sampling', 'detecting', 'aggregating', 'capturing'].includes(status)
  return (
    <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${s.cls}`}>
      <Icon className={`h-3 w-3 mr-1 ${spin ? 'animate-spin' : ''}`} />
      {s.label}
    </span>
  )
}

const IN_FLIGHT: BroadcastStatus[] = [
  'queued', 'estimating', 'sampling', 'detecting', 'aggregating', 'capturing'
]

/**
 * Never analysed, or stopped part-way. Failed and halted RESUME: the scout
 * run keeps every finished segment unless forced, and an already-charged
 * broadcast is never charged again, so neither is the destructive re-run.
 */
const FIRST_RUN: BroadcastStatus[] = ['uploaded', 'failed', 'halted']
const RESUMABLE: BroadcastStatus[] = ['failed', 'halted']
/**
 * Already finished once. Re-running REPLACES the previous results.
 *
 * NOT OFFERED IN THE UI. One click destroys reviewer corrections and
 * exclusions, un-publishes a live broadcast and spends ~$30 of inference, and
 * nobody should be able to do that by misreading an icon. It was opened
 * briefly on 2026-10-06 to re-run after the max_tokens fix and is closed again
 * for UAT.
 *
 * It comes back behind a two-step approval. The constant and the confirmation
 * copy stay so that is wiring, not a rewrite — the backend has never had the
 * restriction, which is worth remembering: this is a UI gate, not a guarantee.
 */
const RERUN: BroadcastStatus[] = ['review', 'published']
/** Anything settled enough to put beyond a customer's reach. */
const ARCHIVABLE: BroadcastStatus[] = [
  'uploaded', 'review', 'published', 'failed', 'halted', 'awaiting_approval'
]
/**
 * Scout says a build is under way. Bounded, because a scout restart mid-build
 * leaves 'building' behind with nothing to clear it; past the remux timeout it
 * is treated as over and the button is offered again.
 */
const REPLAY_BUILD_MAX_MS = 6 * 60 * 60_000
const replayBuilding = (b: Broadcast) =>
  b.playback_state?.status === 'building'
  && Date.now() - Date.parse(b.playback_state.at ?? '') < REPLAY_BUILD_MAX_MS

/** Terminal states where a recorded error_message is actually about NOW. */
const FAILED: BroadcastStatus[] = ['failed', 'halted']

// A run takes minutes, not seconds, so 10s was far finer than the thing it
// watches. Doubles up to POLL_MAX_MS whenever the API rate-limits us.
const POLL_BASE_MS = 15_000
const POLL_MAX_MS = 120_000

// A full game remuxes in minutes in-region. Past this, assume scout dropped
// it (the remux records nothing until it succeeds) and stop watching.
const REPLAY_GIVE_UP_MS = 60 * 60_000
const REPLAY_WATCH_KEY = 'broadcast.replayBuilds'

/** Builds started from this browser, by broadcast id -> start time. Kept in
 *  sessionStorage so a refresh mid-build still gets the completion toast. */
const readReplayWatch = (): Record<string, number> => {
  try { return JSON.parse(sessionStorage.getItem(REPLAY_WATCH_KEY) ?? '{}') } catch { return {} }
}
const writeReplayWatch = (m: Record<string, number>) => {
  try { sessionStorage.setItem(REPLAY_WATCH_KEY, JSON.stringify(m)) } catch { /* private mode */ }
}

const BroadcastLibrary: React.FC<Props> = ({ customerId, organizationName, readOnly = false, onReview }) => {
  const { showError, showSuccess, showWarning } = useToast()
  const [preflight, setPreflight] = useState<{ b: Broadcast; p: BroadcastPreflight } | null>(null)
  const [preparing, setPreparing] = useState<string | null>(null)

  /**
   * Builds Instant Replay for a broadcast that has none.
   *
   * The remux takes minutes on a full game and scout owns it, so the request
   * returns at once. The broadcast then joins `replayBuilds` and the page
   * polls, as it does for analysis, until has_playback flips — then toasts.
   */
  const [replayBuilds, setReplayBuilds] = useState<Record<string, number>>(readReplayWatch)
  const updateReplayBuilds = useCallback((fn: (m: Record<string, number>) => Record<string, number>) => {
    setReplayBuilds((prev) => { const next = fn(prev); writeReplayWatch(next); return next })
  }, [])
  const buildReplay = async (b: Broadcast) => {
    setPreparing(b.id)
    try {
      await broadcastAdminService.preparePlayback(b.id)
      updateReplayBuilds((m) => ({ ...m, [b.id]: Date.now() }))
      showSuccess('Building Instant Replay',
        `"${b.title}" is being prepared. A full game takes a few minutes — you'll get a message here when it's ready.`)
      await load(true)
    } catch (err) {
      showError('Could not start the build',
        err instanceof Error ? err.message : 'Please try again')
    } finally { setPreparing(null) }
  }
  // Archiving is destructive to a customer's view, so it gets the same
  // confirm-then-act shape as Analyse rather than firing on the click.
  const [archiving, setArchiving] = useState<Broadcast | null>(null)
  const [working, setWorking] = useState<string | null>(null)
  const [rows, setRows] = useState<Broadcast[]>([])
  const [loading, setLoading] = useState(true)
  const [showUpload, setShowUpload] = useState(false)
  const [measuring, setMeasuring] = useState<Broadcast | null>(null)
  // Poll delay, in a ref so backing off does not re-render or restart the
  // timer twice. Reset to POLL_BASE_MS on any successful read.
  const pollMs = useRef(POLL_BASE_MS)

  /**
   * `background` is a poll rather than something the user asked for. Two
   * things follow, and both were wrong before:
   *   - it must not touch `loading`, which early-returns the whole table for
   *     a spinner. At a 10s poll the page blinked every 10 seconds for the
   *     entire run.
   *   - it must not toast. A failed background refresh is not a failed
   *     action, and the run itself is unaffected — the next poll picks it up.
   */
  const load = useCallback(async (background = false) => {
    if (!background) setLoading(true)
    try {
      const res = await broadcastAdminService.listBroadcasts(customerId)
      setRows(res.data || [])
      pollMs.current = POLL_BASE_MS
    } catch (err) {
      // 429 is this page's own doing: CRO allows 500 requests per 15 minutes
      // across every route, and a flat 10s poll through a ten-minute run
      // spends that budget alongside the rest of the app. Back off rather
      // than keep hammering, and never report it as a failure to load.
      const status = (err as { status?: number })?.status
      if (status === 429) {
        pollMs.current = Math.min(pollMs.current * 2, POLL_MAX_MS)
      } else if (!background) {
        showError('Failed to load broadcasts', err instanceof Error ? err.message : 'Please try again')
      }
    } finally {
      // No mounted-ref guard. useRef(true) initialises ONCE, so StrictMode's
      // mount/unmount/remount leaves it false forever and setLoading(false)
      // never runs again — the spinner spins for good. React 18 dropped the
      // setState-on-unmounted warning, so the guard bought nothing anyway.
      if (!background) setLoading(false)
    }
  }, [customerId, showError])

  useEffect(() => { void load() }, [load])

  // Analysis is async: the trigger returns at once and the run reports only
  // through status, so poll while anything is mid-flight and stop when it is
  // not. No websocket, no spinner the user has to sit in front of.
  //
  // setTimeout rather than setInterval so a backed-off delay takes effect on
  // the very next tick instead of after the current interval is torn down.
  // A run that dies reports only by changing a column. Polling stops the
  // moment it leaves IN_FLIGHT, so without this the page just goes quiet and
  // the badge changes somewhere below the fold — which is exactly how a
  // 25-minute, $3.50 failure went unnoticed.
  const watched = useRef<Set<string>>(new Set())
  useEffect(() => {
    for (const r of rows) {
      if (IN_FLIGHT.includes(r.status)) { watched.current.add(r.id); continue }
      if (!watched.current.has(r.id)) continue
      watched.current.delete(r.id)
      if (r.status === 'failed' || r.status === 'halted') {
        showError(`Analysis ${r.status}: ${r.title}`,
          r.error_message || 'No reason recorded. Check the service log.')
      } else if (r.status === 'review') {
        showSuccess('Analysis complete', `“${r.title}” is ready to review.`)
      }
    }
  }, [rows, showError, showSuccess])

  // Instant Replay builds report only by has_playback turning true. A failed
  // remux writes nothing, so the give-up timer is the only failure signal.
  useEffect(() => {
    const ids = Object.keys(replayBuilds)
    if (ids.length === 0 || rows.length === 0) return
    const done: string[] = []
    for (const id of ids) {
      const r = rows.find((x) => x.id === id)
      // Another customer's broadcast (the watch list is per browser, the
      // table per customer): leave it for that table, expire it silently.
      if (!r) { if (Date.now() - replayBuilds[id] > REPLAY_GIVE_UP_MS) done.push(id); continue }
      if (r.has_playback) {
        done.push(id)
        showSuccess('Instant Replay ready', `“${r.title}” can now be replayed.`)
      } else if (r.playback_state?.status === 'failed'
                 && Date.parse(r.playback_state.at ?? '') >= replayBuilds[id] - 60_000) {
        // Scout records a failed build now, so there is no need to wait out
        // the timer below to say so.
        done.push(id)
        showError('Instant Replay failed', `“${r.title}”: ${r.playback_state.error ?? 'no reason recorded'}`)
      } else if (Date.now() - replayBuilds[id] > REPLAY_GIVE_UP_MS) {
        done.push(id)
        showWarning('Instant Replay not ready',
          `“${r.title}” has been building for over an hour. It may have failed — check the scout log, then try again.`)
      }
    }
    if (done.length > 0) {
      updateReplayBuilds((m) => {
        const next = { ...m }
        for (const id of done) delete next[id]
        return next
      })
    }
  }, [rows, replayBuilds, showSuccess, showError, showWarning, updateReplayBuilds])

  const inFlight = rows.some((r) => IN_FLIGHT.includes(r.status) || !!replayBuilds[r.id] || replayBuilding(r))
  useEffect(() => {
    if (!inFlight) { pollMs.current = POLL_BASE_MS; return }
    const t = setTimeout(() => { void load(true) }, pollMs.current)
    return () => clearTimeout(t)
  }, [inFlight, rows, load])

  // The backend never cared about status — it clears prior results and
  // replays — so the only thing preventing a re-run was this table offering
  // no button for one.
  const askAnalyse = async (b: Broadcast) => {
    setWorking(b.id)
    try {
      const res = await broadcastAdminService.preflightBroadcast(customerId, b.id)
      setPreflight({ b, p: res.data })
    } catch (err) {
      showError('Could not estimate', err instanceof Error ? err.message : 'Please try again')
    } finally {
      setWorking(null)
    }
  }

  const confirmArchive = async () => {
    if (!archiving) return
    setWorking(archiving.id)
    try {
      await broadcastAdminService.archiveBroadcast(customerId, archiving.id)
      showSuccess('Archived', `“${archiving.title}” is no longer visible to the customer.`)
      setArchiving(null)
      await load()
    } catch (err) {
      showError('Could not archive', err instanceof Error ? err.message : 'Please try again')
    } finally { setWorking(null) }
  }

  const restore = async (b: Broadcast) => {
    setWorking(b.id)
    try {
      const res = await broadcastAdminService.restoreBroadcast(customerId, b.id)
      const status = (res.data as { status?: string } | undefined)?.status
      showSuccess(status === 'review' ? 'Restored to review' : 'Restored',
        status === 'review'
          ? 'It is back in the review queue. Publish it again to put it in front of the customer.'
          : 'It is back where it was before it was archived.')
      await load()
    } catch (err) {
      showError('Could not restore', err instanceof Error ? err.message : 'Please try again')
    } finally { setWorking(null) }
  }

  const [editing, setEditing] = useState<Broadcast | null>(null)
  const [resetting, setResetting] = useState<Broadcast | null>(null)
  const confirmReset = async () => {
    if (!resetting) return
    setWorking(resetting.id)
    try {
      await broadcastAdminService.resetRun(resetting.id)
      showSuccess('Run reset', `“${resetting.title}” is marked Failed. Analyse it to resume from the finished segments — no further credits.`)
      setResetting(null)
      await load()
    } catch (err) {
      showError('Could not reset', err instanceof Error ? err.message : 'Please try again')
    } finally { setWorking(null) }
  }

  const confirmAnalyse = async () => {
    if (!preflight) return
    setWorking(preflight.b.id)
    try {
      await broadcastAdminService.analyzeBroadcast(customerId, preflight.b.id)
      showSuccess('Analysis started', 'You can carry on — progress shows in the Status column.')
      setPreflight(null)
      await load()
    } catch (err) {
      showError('Could not start analysis', err instanceof Error ? err.message : 'Please try again')
    } finally {
      setWorking(null)
    }
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center py-16 text-gray-500">
        <Loader2 className="h-5 w-5 animate-spin mr-2" /> Loading broadcasts…
      </div>
    )
  }

  return (
    <div className="space-y-4">
      {!readOnly && (
        <div className="flex items-center justify-between">
          <p className="text-xs text-gray-500">
            {rows.length} broadcast{rows.length === 1 ? '' : 's'}
          </p>
          <button
            onClick={() => setShowUpload(true)}
            className="inline-flex items-center px-3 py-1.5 bg-gradient-to-r from-blue-600 to-blue-700 text-white text-xs font-medium rounded-lg hover:from-blue-700 hover:to-blue-800 transition-all shadow-sm"
          >
            <Plus className="h-3.5 w-3.5 mr-1.5" /> Add broadcast
          </button>
        </div>
      )}

      {/* Outside the readOnly guard: measurement details are support-only in
          practice because the library is only rendered read-only for a
          customer, but the drawer gates nothing itself and tying it to the
          upload affordance would be an accident waiting to be relied on. */}
      <BroadcastMeasurementDrawer
        isOpen={measuring !== null}
        broadcast={measuring}
        customerId={customerId}
        onClose={() => setMeasuring(null)}
        onSaved={() => { void load() }}
      />

      {!readOnly && (
        <BroadcastUploadForm
          isOpen={showUpload}
          customerId={customerId}
          organizationName={organizationName}
          onDone={async () => { setShowUpload(false); await load() }}
          onCancel={() => setShowUpload(false)}
        />
      )}
      {!readOnly && (
        <BroadcastUploadForm
          isOpen={editing !== null}
          editing={editing}
          customerId={customerId}
          organizationName={organizationName}
          onDone={async () => { setEditing(null); await load() }}
          onCancel={() => setEditing(null)}
        />
      )}

      {resetting && (
        <div className="rounded-lg border p-4 bg-amber-50 border-amber-200">
          <h4 className="text-sm font-semibold text-gray-900 mb-2">
            Reset the stuck run on “{resetting.title}”?
          </h4>
          <p className="text-xs text-amber-900 mb-3 flex items-start gap-1.5">
            <AlertTriangle className="h-3.5 w-3.5 flex-shrink-0 mt-0.5" />
            <span>
              For an analysis that stopped without finishing — typically after a deploy or
              crash, with the status unchanged for a long time. If it is still running this
              is refused. It is marked Failed; Analyse then resumes from the segments already
              finished, and no further credits are charged.
            </span>
          </p>
          <div className="flex gap-2">
            <button onClick={confirmReset} disabled={working === resetting.id}
              className="inline-flex items-center px-3 py-1.5 bg-amber-600 text-white text-xs font-medium rounded-lg hover:bg-amber-700 disabled:opacity-50 transition-colors">
              {working === resetting.id
                ? <><Loader2 className="h-3.5 w-3.5 mr-1.5 animate-spin" />Resetting…</>
                : <><RotateCcw className="h-3.5 w-3.5 mr-1.5" />Reset run</>}
            </button>
            <button onClick={() => setResetting(null)}
              className="px-3 py-1.5 bg-white border border-gray-300 text-gray-700 text-xs font-medium rounded-lg hover:bg-gray-50">
              Cancel
            </button>
          </div>
        </div>
      )}

      {archiving && (
        <div className="rounded-lg border p-4 bg-amber-50 border-amber-200">
          <h4 className="text-sm font-semibold text-gray-900 mb-2">
            Archive “{archiving.title}”?
          </h4>
          <p className="text-xs text-amber-900 mb-3 flex items-start gap-1.5">
            <AlertTriangle className="h-3.5 w-3.5 flex-shrink-0 mt-0.5" />
            <span>
              {archiving.status === 'published'
                ? 'This broadcast is PUBLISHED. Archiving removes it from the customer’s library immediately.'
                : 'It will not reach the customer and leaves the review queue.'}
              {' '}Nothing is deleted: the analysis, its brands and its cost all stay, and it can be
              restored to review later.
            </span>
          </p>
          <p className="text-xs text-gray-500 mb-3">
            Credits are not refunded — archiving is an editorial decision, not a billing one. To
            give a credit back, add an adjustment on the Entitlement tab so the reason is recorded.
          </p>
          <div className="flex gap-2">
            <button onClick={confirmArchive} disabled={working === archiving.id}
              className="inline-flex items-center px-3 py-1.5 bg-amber-600 text-white text-xs font-medium rounded-lg hover:bg-amber-700 disabled:opacity-50 transition-colors">
              {working === archiving.id
                ? <><Loader2 className="h-3.5 w-3.5 mr-1.5 animate-spin" />Archiving…</>
                : <><Archive className="h-3.5 w-3.5 mr-1.5" />Archive</>}
            </button>
            <button onClick={() => setArchiving(null)}
              className="px-3 py-1.5 bg-white border border-gray-300 text-gray-700 text-xs font-medium rounded-lg hover:bg-gray-50">
              Cancel
            </button>
          </div>
        </div>
      )}

      {preflight && (
        <div className={`rounded-lg border p-4 ${preflight.p.ok ? 'bg-blue-50/50 border-blue-200' : 'bg-amber-50 border-amber-200'}`}>
          <h4 className="text-sm font-semibold text-gray-900 mb-2">
            {RERUN.includes(preflight.b.status) ? 'Re-analyse'
              : RESUMABLE.includes(preflight.b.status) ? 'Resume' : 'Analyse'} “{preflight.b.title}”?
          </h4>
          {RESUMABLE.includes(preflight.b.status) && (
            <p className="text-xs text-gray-600 mb-3">
              Segments the last run finished are kept and only the rest are analysed.
              {preflight.b.credits_charged != null && ' This broadcast was already charged, so no further credits are used.'}
            </p>
          )}
          {RERUN.includes(preflight.b.status) && (
            <p className="text-xs text-amber-900 mb-3 flex items-start gap-1.5">
              <AlertTriangle className="h-3.5 w-3.5 flex-shrink-0 mt-0.5" />
              <span>
                This replaces every detection, appearance and coverage row from the
                previous run, including any zone corrections or exclusions a reviewer
                made.
                {preflight.b.status === 'published' && ' The broadcast returns to review '
                  + 'and disappears from the customer’s library until it is published again.'}
              </span>
            </p>
          )}
          <div className="grid grid-cols-4 gap-3 mb-3">
            {[
              ['Frames', preflight.p.frames.toLocaleString()],
              ['Credits', String(preflight.p.creditsRequired)],
              ['Remaining after', preflight.p.creditsRemaining == null ? '—'
                : String(preflight.p.creditsRemaining - preflight.p.creditsRequired)],
              ['Our cost', `$${preflight.p.estimatedCostUsd.toFixed(2)}`]
            ].map(([k, v]) => (
              <div key={k}>
                <p className="text-xs font-semibold text-gray-500 uppercase tracking-wider">{k}</p>
                <p className="text-lg font-bold text-gray-900">{v}</p>
              </div>
            ))}
          </div>
          {!preflight.p.ok && (
            <p className="text-xs text-amber-900 mb-3 flex items-start gap-1.5">
              <AlertTriangle className="h-3.5 w-3.5 flex-shrink-0 mt-0.5" />
              {preflight.p.reason}
            </p>
          )}
          <p className="text-xs text-gray-500 mb-3">
            Credits are charged when analysis starts. “Our cost” is the inference spend,
            not the customer’s bill.
          </p>
          <div className="flex gap-2">
            <button onClick={confirmAnalyse} disabled={!preflight.p.ok || working === preflight.b.id}
              className="inline-flex items-center px-3 py-1.5 bg-gradient-to-r from-blue-600 to-blue-700 text-white text-xs font-medium rounded-lg hover:from-blue-700 hover:to-blue-800 disabled:opacity-50 disabled:cursor-not-allowed transition-all">
              {working === preflight.b.id
                ? <><Loader2 className="h-3.5 w-3.5 mr-1.5 animate-spin" />Starting…</>
                : <><Play className="h-3.5 w-3.5 mr-1.5" />
                    {RERUN.includes(preflight.b.status) ? 'Replace results'
                      : RESUMABLE.includes(preflight.b.status) ? 'Resume analysis' : 'Start analysis'}</>}
            </button>
            <button onClick={() => setPreflight(null)}
              className="px-3 py-1.5 bg-white border border-gray-300 text-gray-700 text-xs font-medium rounded-lg hover:bg-gray-50">
              Cancel
            </button>
          </div>
        </div>
      )}

      {rows.length === 0 ? (
        <div className="bg-white rounded-lg border border-gray-200 py-14 text-center">
          <Film className="h-8 w-8 text-gray-300 mx-auto mb-2" />
          <p className="text-sm font-medium text-gray-600">No broadcasts yet</p>
          <p className="text-xs text-gray-400 mt-1">
            {readOnly
              ? 'Broadcasts appear here once your team has uploaded them.'
              : 'Add a game file to get started.'}
          </p>
        </div>
      ) : (
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
          <table className="w-full text-sm">
            <thead className="bg-gray-50 border-b border-gray-200">
              <tr className="text-left">
                {['Broadcast', 'Event date', 'Duration', 'Size', 'Credits', 'Status'].map((h) => (
                  <th key={h} className="px-4 py-2 text-xs font-semibold text-gray-600 uppercase tracking-wider">{h}</th>
                ))}
                {!readOnly && <th className="px-4 py-2 text-right text-xs font-semibold text-gray-600 uppercase tracking-wider">Actions</th>}
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {rows.map((b) => (
                <tr key={b.id} className="hover:bg-gray-50">
                  <td className="px-4 py-2">
                    <p className="font-medium text-gray-900">{b.title}</p>
                    <p className="text-xs text-gray-500">
                      {[b.home_team_name && b.away_team_name ? `${b.away_team_name} @ ${b.home_team_name}` : null,
                        b.league_short_name, b.venue_name,
                        b.competition_stage_name, b.season]
                        .filter(Boolean).join(' · ') || '—'}
                      {b.child_count > 0 && (
                        <span className="ml-1.5 px-1.5 py-0.5 rounded bg-blue-50 text-blue-700 text-[10px] font-medium">
                          {b.child_count + 1} files, one event
                        </span>
                      )}
                    </p>
                  </td>
                  <td className="px-4 py-2 text-gray-600 whitespace-nowrap">{fmtDate(b.event_date)}</td>
                  <td className="px-4 py-2 text-gray-600 whitespace-nowrap">{fmtDuration(b.duration_ms)}</td>
                  <td className="px-4 py-2 text-gray-600 whitespace-nowrap">{fmtSize(b.file_size_bytes)}</td>
                  <td className="px-4 py-2 text-gray-600 whitespace-nowrap">
                    {b.credits_charged != null ? b.credits_charged : <span className="text-gray-300">not yet</span>}
                  </td>
                  <td className="px-4 py-2">
                    <div className="flex items-center gap-1.5">
                      <StatusBadge status={b.status} />
                      {/* Gated on STATUS, not on the message being present.
                          error_message is not cleared when a run restarts, so
                          a broadcast that failed yesterday and is analysing
                          happily right now still carries yesterday's text —
                          and this put a red warning beside a green status. */}
                      {b.error_message && FAILED.includes(b.status) && (
                        <Tooltip content={b.error_message} placement="left" maxWidth={420}>
                          <AlertTriangle className="h-3.5 w-3.5 text-red-500 flex-shrink-0 cursor-help" />
                        </Tooltip>
                      )}
                    </div>
                    {b.error_message && FAILED.includes(b.status) && (
                      <p className="text-[11px] text-red-600 mt-1 max-w-md line-clamp-2"
                         title={b.error_message}>
                        {b.error_message}
                      </p>
                    )}
                  </td>
                  {!readOnly && (
                    <td className="px-4 py-2 text-right">
                      <div className="flex items-center justify-end gap-0.5">
                        <Tooltip
                          content={RESUMABLE.includes(b.status) ? 'Resume analysis — finished segments are kept'
                            : FIRST_RUN.includes(b.status) ? 'Analyse' : 'Already analysed'}
                          placement="left"
                        >
                          <span>
                            <button
                              onClick={() => askAnalyse(b)}
                              disabled={!FIRST_RUN.includes(b.status) || working === b.id}
                              className="p-1.5 text-gray-600 hover:text-blue-600 hover:bg-blue-50 rounded-lg transition-colors disabled:opacity-30 disabled:hover:text-gray-600 disabled:hover:bg-transparent disabled:cursor-not-allowed"
                            >
                              {working === b.id
                                ? <Loader2 className="h-3.5 w-3.5 animate-spin" />
                                : <Play className="h-3.5 w-3.5" />}
                            </button>
                          </span>
                        </Tooltip>
                        {IN_FLIGHT.includes(b.status) && (
                          <Tooltip content="Reset a stuck run — for an analysis that stopped without finishing" placement="left" maxWidth={240}>
                            <span>
                              <button
                                onClick={() => setResetting(b)}
                                disabled={working === b.id}
                                className="p-1.5 text-gray-600 hover:text-amber-700 hover:bg-amber-50 rounded-lg transition-colors disabled:opacity-30"
                              >
                                <RotateCcw className="h-3.5 w-3.5" />
                              </button>
                            </span>
                          </Tooltip>
                        )}
                        <Tooltip content="Edit details — title, date, league, teams, venue" placement="left">
                          <span>
                            <button
                              onClick={() => setEditing(b)}
                              className="p-1.5 text-gray-600 hover:text-blue-600 hover:bg-blue-50 rounded-lg transition-colors"
                            >
                              <Pencil className="h-3.5 w-3.5" />
                            </button>
                          </span>
                        </Tooltip>
                        <Tooltip
                          content={b.status === 'archived'
                            ? 'Restore — analysed broadcasts return to review; others to where they were'
                            : ARCHIVABLE.includes(b.status)
                              ? 'Archive — keep the analysis, keep it away from the customer'
                              : 'Cannot archive mid-analysis'}
                          placement="left"
                        >
                          <span>
                            <button
                              onClick={() => b.status === 'archived' ? void restore(b) : setArchiving(b)}
                              disabled={(!ARCHIVABLE.includes(b.status) && b.status !== 'archived') || working === b.id}
                              className="p-1.5 text-gray-600 hover:text-amber-700 hover:bg-amber-50 rounded-lg transition-colors disabled:opacity-30 disabled:hover:text-gray-600 disabled:hover:bg-transparent disabled:cursor-not-allowed"
                            >
                              {b.status === 'archived'
                                ? <ArchiveRestore className="h-3.5 w-3.5" />
                                : <Archive className="h-3.5 w-3.5" />}
                            </button>
                          </span>
                        </Tooltip>
                        {/* Priced = a 30-second rate exists, whether from a rate
                            card or an override. Checking the override alone left
                            every card-priced broadcast flagged amber. */}
                        <Tooltip
                          content={b.rate_per_30s == null
                            ? 'Measurement details — not priced yet'
                            : 'Measurement details'}
                          placement="left"
                        >
                          <span>
                            <button
                              onClick={() => setMeasuring(b)}
                              className={`p-1.5 rounded-lg transition-colors hover:bg-emerald-50 ${
                                b.rate_per_30s == null
                                  ? 'text-amber-600 hover:text-amber-700'
                                  : 'text-gray-600 hover:text-emerald-700'
                              }`}
                            >
                              <SlidersHorizontal className="h-3.5 w-3.5" />
                            </button>
                          </span>
                        </Tooltip>
                        {/* ONLY WHEN IT IS MISSING. A broadcast that has
                            Instant Replay needs no button, and an always-on
                            one invites a rebuild of a 5GB file for no
                            reason. Uploads from before the remux shipped,
                            and remuxes that failed, are the two cases — and
                            until now there was no way to fix either from
                            the UI even though the endpoint existed. */}
                        {(b.has_playback === false || replayBuilding(b)) && b.status !== 'archived' && (
                          <Tooltip
                            content={replayBuilds[b.id] || replayBuilding(b)
                              ? 'Building Instant Replay… you will get a message when it is ready.'
                              : b.playback_state?.status === 'failed'
                                ? `The last Instant Replay build failed: ${b.playback_state.error ?? 'no reason recorded'}. Click to try again.`
                                : 'Instant Replay has not been built for this broadcast. Building it takes a few minutes and only happens once.'}
                            placement="left"
                            maxWidth={280}
                          >
                            <span>
                              <button
                                onClick={() => void buildReplay(b)}
                                disabled={preparing === b.id || !!replayBuilds[b.id] || replayBuilding(b)}
                                className={`p-1.5 rounded-lg transition-colors disabled:opacity-40 ${
                                  b.playback_state?.status === 'failed'
                                    ? 'text-red-600 hover:text-red-700 hover:bg-red-50'
                                    : 'text-amber-600 hover:text-amber-700 hover:bg-amber-50'}`}
                              >
                                {preparing === b.id || replayBuilds[b.id] || replayBuilding(b)
                                  ? <Loader2 className="h-3.5 w-3.5 animate-spin" />
                                  : <Film className="h-3.5 w-3.5" />}
                              </button>
                            </span>
                          </Tooltip>
                        )}
                        <Tooltip
                          // Published opens too. It is read-only in there, but
                          // it was previously reachable ONLY via the review
                          // tab's dropdown, so removing that would have stranded
                          // every published broadcast with no way back in.
                          content={b.status === 'review'
                            ? 'Review and publish'
                            : b.status === 'published'
                              ? 'View published review'
                              : 'Not yet analysed'}
                          placement="left"
                        >
                          <span>
                            <button
                              onClick={() => onReview?.(b)}
                              disabled={(b.status !== 'review' && b.status !== 'published') || !onReview}
                              className="p-1.5 text-gray-600 hover:text-blue-600 hover:bg-blue-50 rounded-lg transition-colors disabled:opacity-30 disabled:hover:text-gray-600 disabled:hover:bg-transparent disabled:cursor-not-allowed"
                            >
                              <CheckSquare className="h-3.5 w-3.5" />
                            </button>
                          </span>
                        </Tooltip>
                      </div>
                    </td>
                  )}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  )
}

export default BroadcastLibrary
